/**
 * Follow-Up Reminder Workflow
 *
 * This workflow handles the automatic detection of contacts requiring follow-up:
 * 1. Scans all contacts for recent interaction history
 * 2. Classifies priority based on last contact date and importance
 * 3. Sends notifications suggesting follow-ups for inactive relationships
 */

import type { Workflow } from './types';

// =============================================================================
// WORKFLOW DEFINITION
// =============================================================================

export const followUpReminderWorkflow: Workflow = {
  id: 'follow-up-reminder',
  name: 'Promemoria Follow-Up Contatti',
  description: 'Analizza i contatti inattivi e suggerisce follow-up in base alla priorità',
  trigger: {
    type: 'schedule',
    config: {
      // Run daily at 9 AM
      cron: '0 9 * * *',
      timezone: 'Europe/Rome',
    },
  },
  steps: [
    {
      id: 'scan_contacts',
      name: 'Scansiona Contatti',
      type: 'custom',
      config: {
        // Find contacts with no interaction in last X days
        inactivityThreshold: 30, // days
        includeTypes: ['client', 'lead', 'partner'],
        excludeArchived: true,
      },
      next: ['classify_priority'],
    },
    {
      id: 'classify_priority',
      name: 'Classifica Priorità',
      type: 'ai_analyze',
      config: {
        prompt: `Analyze these contacts and classify their follow-up priority.
For each contact, consider:
- Days since last interaction
- Contact type and importance
- Past interaction frequency
- Business relationship value

Respond in JSON:
{
  "followUps": [
    {
      "contactId": "...",
      "contactName": "...",
      "priority": "high|medium|low",
      "reason": "Brief explanation",
      "suggestedAction": "Suggested follow-up action"
    }
  ]
}`,
      },
      next: ['send_notification'],
    },
    {
      id: 'send_notification',
      name: 'Invia Notifica',
      type: 'send_notification',
      config: {
        type: 'in_app',
        message: 'Ho trovato {count} contatti che necessitano follow-up.',
        includeDetails: true,
        groupByPriority: true,
      },
    },
  ],
  enabled: true,
  version: 1,
  createdAt: new Date().toISOString(),
  updatedAt: new Date().toISOString(),
};

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

/**
 * Calculate inactivity score based on last interaction
 */
export function calculateInactivityScore(lastInteractionDate: string, contactType: string): number {
  const now = new Date();
  const lastContact = new Date(lastInteractionDate);
  const daysSince = Math.floor((now.getTime() - lastContact.getTime()) / (1000 * 60 * 60 * 24));

  // Weight by contact type
  const typeMultiplier = {
    client: 1.5,
    lead: 1.2,
    partner: 1.3,
    other: 1.0,
  }[contactType] || 1.0;

  return daysSince * typeMultiplier;
}
